import { createSelector } from "reselect";
import type { AppState } from "src/store";
import { getAllSales } from "src/store/sale/selector";
import { expensesSelector } from "src/store/transaction/selector";
import { generalLedgerAccountsSelector } from "src/store/glAccounts/selector";
import { selectUser } from "src/store/system/selector";
import type { Sale } from "src/interfaces/sale.interface";
import type { Transaction } from "src/interfaces/transaction.interface";
import type { User } from "src/interfaces/systemState.interface";
import type {
  GeneralLedgerAccountV2,
  GeneralLedgerAccountV2Type,
} from "src/interfaces/generalLedgerAccount.interface";
import { getGeneralLedgerAccountTypeByName } from "src/utils/general-ledger-accounts";
import {
  endOfCalendarDayContainingInstant,
  formatZonedDate,
  getDateTimePartsInTimeZone,
  getEffectiveTimeZoneFromUser,
  startOfCalendarDayContainingInstant,
  zonedMonthRangeFromZeroIndexedMonth,
  zonedQuarterRange,
} from "src/utils/timezone";
import {
  TransactionFees,
  CostOfSoldInventory,
  ShippingCost,
} from "./constants";
import type {
  DateParametersProfitLoss,
  ParametersProfitLossComparison,
  ParametersSalesTax,
  ProfitLossComparisonReport,
  ProfitLossReport,
  ProfitLossReportData,
  ReportParameters,
  SalesTaxReport,
} from "./report";

function renderOnlyDate(date: Date, timeZone?: string): string {
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    ...(timeZone ? { timeZone } : null),
  });
}

function renderZonedOnlyDate(date: Date, timeZone: string): string {
  return formatZonedDate(date, timeZone, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
}

function pushOrCreate<T>(
  obj: Record<string, T[]>,
  section: string,
  item: T
): void {
  if (!obj[section]) obj[section] = [];

  obj[section].push(item);
}

function buildProfitLossReportData(
  reportType: "profit/loss",
  parameters: DateParametersProfitLoss,
  generalLedgerAccounts: GeneralLedgerAccountV2[],
  sales: Sale[],
  transactions: (Transaction & {
    accountType: GeneralLedgerAccountV2Type | undefined;
  })[],
  user?: User
): ProfitLossReportData {
  const reportDetails = {
    title: "Profit/Loss Statement",
    titleFull: "",
    filename: "",
    dateRange: `${renderOnlyDate(
      parameters.startDate,
      user?.timezone
    )} to ${renderOnlyDate(parameters.endDate, user?.timezone)}`,
  };

  reportDetails.titleFull = `${reportDetails.title} from ${reportDetails.dateRange}`;
  reportDetails.filename =
    `${reportDetails.title}-${reportDetails.dateRange}.pdf`
      .toLowerCase()
      .replace(/[-/]/g, "-");

  const salesByDepartment: Record<string, number> = {};
  const COGSDetail: Record<string, number | undefined> = {
    [TransactionFees]: undefined,
    [CostOfSoldInventory]: undefined,
    [ShippingCost]: undefined,
    "Shipping Supplies": undefined,
    "Shipping Fees": undefined,
    "Sourcing Mileage/Transportation": undefined,
    "Listing/Platform Fees": undefined,
    "Inventory Purchases": undefined,
  };

  for (const account of generalLedgerAccounts) {
    if (
      !account.default &&
      account.type[0] === "expenses" &&
      account.type[1] === "cost-of-goods-sold"
    ) {
      COGSDetail[`${account.name}`] = undefined;
    }
  }

  const expenseDetail: any = {};
  let totalSales = 0;
  let totalCOGS = 0;
  let totalExpenses = 0;

  const timeZone = getEffectiveTimeZoneFromUser(user);
  const startDate = startOfCalendarDayContainingInstant(
    new Date(parameters.startDate),
    timeZone
  );
  const endDate = endOfCalendarDayContainingInstant(
    new Date(parameters.endDate),
    timeZone
  );

  const salesListByDepartment: Record<string, Sale[]> = {};
  const transactionsListByExpense: Record<string, Transaction[]> = {};

  for (const sale of sales) {
    const saleDate = new Date(
      (sale.original_id || sale.is_return
        ? sale.return_date
        : sale.sale_date) || 0
    );
    if (!saleDate.valueOf() || saleDate < startDate || saleDate > endDate)
      continue;

    totalSales += +sale.sale_price;

    let platform = sale.sale_platform || "Uncategorized";
    if (sale.original_id || sale.is_return) platform = `${platform} (Refunds)`;

    if (!salesByDepartment[platform]) salesByDepartment[platform] = 0;
    salesByDepartment[platform] += +sale.sale_price;

    pushOrCreate(salesListByDepartment, platform, sale);
  }

  for (const transaction of transactions) {
    if (
      !transaction.date?.valueOf() ||
      transaction.date < startDate ||
      transaction.date > endDate
    )
      continue;
    const accountType = transaction.accountType?.[0];
    if (accountType === "income") {
      let account = `${transaction.account || "Uncategorized"}`;
      const amount = -1 * (+transaction.amount || 0);
      if (amount < 0) account = `${account} (Refund)`;
      if (!salesByDepartment[account]) salesByDepartment[account] = 0;
      salesByDepartment[account] += amount;
      totalSales += amount;
      pushOrCreate(transactionsListByExpense, account, transaction);
    } else if (accountType === "expenses") {
      if (transaction.account in COGSDetail) {
        COGSDetail[transaction.account] =
          (COGSDetail[transaction.account] || 0) + Number(transaction.amount);

        if (transaction.amount)
          pushOrCreate(
            transactionsListByExpense,
            transaction.account,
            transaction
          );
        totalCOGS += +transaction.amount;
      } else {
        if (!expenseDetail[transaction.account])
          expenseDetail[transaction.account] = 0;

        expenseDetail[transaction.account] += transaction.amount;
        totalExpenses += +transaction.amount;

        if (transaction.amount)
          pushOrCreate(
            transactionsListByExpense,
            transaction.account,
            transaction
          );
      }
    }
  }

  const grossProfit = totalSales - totalCOGS;
  const estimatedIncomeTax =
    (grossProfit - totalExpenses) * (user?.income_tax_rate || 0);
  const netProfit = grossProfit - totalExpenses - estimatedIncomeTax;
  const netProfitNoIncomeTax = grossProfit - totalExpenses;

  if (COGSDetail[ShippingCost] !== undefined) {
    COGSDetail["Shipping Fees"] =
      (COGSDetail[ShippingCost] || 0) + (COGSDetail["Shipping Fees"] || 0);
  }

  return {
    salesByDepartment,
    COGSDetail: Object.keys(COGSDetail).reduce((obj, key) => {
      const value = COGSDetail[key];
      if (value !== undefined) obj[key] = value;
      return obj;
    }, {} as Record<string, number>),
    expenseDetail,
    totalSales,
    totalCOGS,
    totalExpenses,
    grossProfit,
    estimatedIncomeTax,
    netProfit,
    netProfitNoIncomeTax,
    reportDetails,
    salesListByDepartment,
    transactionsListByExpense,
  };
}

type PeriodType = "month" | "quarter";

/**
 * Splits [startDate, endDate] into calendar months or quarters in `timeZone`,
 * clamping each bucket to the inclusive range (same semantics as the main P/L
 * report, not browser-local month boundaries).
 */
function getPeriods(
  startDate: Date,
  endDate: Date,
  periodType: PeriodType,
  timeZone: string
): { start: Date; end: Date }[] {
  const rangeStart = startOfCalendarDayContainingInstant(
    new Date(startDate),
    timeZone
  );
  const rangeEnd = endOfCalendarDayContainingInstant(
    new Date(endDate),
    timeZone
  );

  const startParts = getDateTimePartsInTimeZone(rangeStart, timeZone);
  const endParts = getDateTimePartsInTimeZone(rangeEnd, timeZone);

  const periods: { start: Date; end: Date }[] = [];

  if (periodType === "month") {
    let y = startParts.year;
    let m = startParts.month;
    const endY = endParts.year;
    const endM = endParts.month;

    while (y < endY || (y === endY && m <= endM)) {
      const { start: periodStart, end: periodEnd } =
        zonedMonthRangeFromZeroIndexedMonth(y, m - 1, timeZone);
      const clampedStart = periodStart < rangeStart ? rangeStart : periodStart;
      const clampedEnd = periodEnd > rangeEnd ? rangeEnd : periodEnd;
      if (clampedStart <= clampedEnd) {
        periods.push({ start: clampedStart, end: clampedEnd });
      }
      m += 1;
      if (m > 12) {
        m = 1;
        y += 1;
      }
    }
    return periods;
  }

  let y = startParts.year;
  let q = (Math.floor((startParts.month - 1) / 3) + 1) as 1 | 2 | 3 | 4;
  const endY = endParts.year;
  const endQ = (Math.floor((endParts.month - 1) / 3) + 1) as 1 | 2 | 3 | 4;

  while (y < endY || (y === endY && q <= endQ)) {
    const { start: periodStart, end: periodEnd } = zonedQuarterRange(
      y,
      q,
      timeZone
    );
    const clampedStart = periodStart < rangeStart ? rangeStart : periodStart;
    const clampedEnd = periodEnd > rangeEnd ? rangeEnd : periodEnd;
    if (clampedStart <= clampedEnd) {
      periods.push({ start: clampedStart, end: clampedEnd });
    }
    if (q === 4) {
      q = 1;
      y += 1;
    } else {
      q = (q + 1) as 1 | 2 | 3 | 4;
    }
  }

  return periods;
}

function isFullCalendarMonthPeriod(
  periodStart: Date,
  periodEnd: Date,
  timeZone: string
): boolean {
  const startParts = getDateTimePartsInTimeZone(periodStart, timeZone);
  const endParts = getDateTimePartsInTimeZone(periodEnd, timeZone);
  if (
    startParts.year !== endParts.year ||
    startParts.month !== endParts.month
  ) {
    return false;
  }

  const { start: monthStart, end: monthEnd } =
    zonedMonthRangeFromZeroIndexedMonth(
      startParts.year,
      startParts.month - 1,
      timeZone
    );

  return (
    periodStart.getTime() === monthStart.getTime() &&
    periodEnd.getTime() === monthEnd.getTime()
  );
}

function formatProfitLossComparisonColumnLabel(
  periodStart: Date,
  periodEnd: Date,
  groupBy: "month" | "quarter",
  timeZone: string
): string | null {
  if (
    groupBy === "month" &&
    isFullCalendarMonthPeriod(periodStart, periodEnd, timeZone)
  ) {
    return formatZonedDate(periodStart, timeZone, {
      month: "short",
      year: "2-digit",
    });
  }

  return null;
}

const parametersInput = (_state: AppState, parameters: ReportParameters) =>
  parameters;

function buildProfitLossReport(
  reportType: ProfitLossReport["type"],
  parameters: DateParametersProfitLoss,
  generalLedgerAccounts: GeneralLedgerAccountV2[],
  sales: Sale[],
  transactions: Transaction[],
  user?: User
): ProfitLossReport {
  const transactionsWithAccountType = transactions
    .filter((t) => (t as any).account !== "Sales Tax Payable")
    .map((t) => {
      if ((t as any).type === "sale" || "sale_property" in t) {
        switch (t.account) {
          case "Transaction Fees":
          case "Shipping Fees":
          case "Cost of Sold Inventory":
            return {
              ...t,
              accountType: [
                "expenses",
                "cost-of-goods-sold",
              ] as GeneralLedgerAccountV2Type,
            };

          default:
            return {
              ...t,
              accountType: [
                "expenses",
                "operating",
              ] as GeneralLedgerAccountV2Type,
            };
        }
      }

      return {
        ...t,
        accountType: getGeneralLedgerAccountTypeByName(
          generalLedgerAccounts,
          t.account
        ),
      };
    });

  const data = [
    buildProfitLossReportData(
      reportType,
      parameters,
      generalLedgerAccounts,
      sales,
      transactionsWithAccountType,
      user
    ),
  ];

  if (parameters.compareStartDate && parameters.compareEndDate) {
    data.push(
      buildProfitLossReportData(
        reportType,
        {
          startDate: parameters.compareStartDate,
          endDate: parameters.compareEndDate,
        },
        generalLedgerAccounts,
        sales,
        transactionsWithAccountType,
        user
      )
    );
  }

  return {
    type: "profit/loss",
    data,
  };
}

function buildProfitLossComparisonReport(
  reportType: ProfitLossComparisonReport["type"],
  parameters: ParametersProfitLossComparison,
  generalLedgerAccounts: GeneralLedgerAccountV2[],
  sales: Sale[],
  transactions: Transaction[],
  user?: User
): ProfitLossComparisonReport {
  const transactionsWithAccountType = transactions
    .filter((t) => (t as any).account !== "Sales Tax Payable")
    .map((t) => {
      if ((t as any).type === "sale" || "sale_property" in t) {
        switch (t.account) {
          case "Transaction Fees":
          case "Shipping Fees":
          case "Cost of Sold Inventory":
            return {
              ...t,
              accountType: [
                "expenses",
                "cost-of-goods-sold",
              ] as GeneralLedgerAccountV2Type,
            };

          default:
            return {
              ...t,
              accountType: [
                "expenses",
                "operating",
              ] as GeneralLedgerAccountV2Type,
            };
        }
      }

      return {
        ...t,
        accountType: getGeneralLedgerAccountTypeByName(
          generalLedgerAccounts,
          t.account
        ),
      };
    });

  const groupBy = parameters.groupBy || "month";
  const timeZone = getEffectiveTimeZoneFromUser(user);
  const periods =
    groupBy === "month"
      ? getPeriods(parameters.startDate, parameters.endDate, "month", timeZone)
      : getPeriods(
          parameters.startDate,
          parameters.endDate,
          "quarter",
          timeZone
        );

  const data = periods.map((period) => {
    const reportData = buildProfitLossReportData(
      "profit/loss",
      { startDate: period.start, endDate: period.end },
      generalLedgerAccounts,
      sales,
      transactionsWithAccountType,
      user
    );

    const columnLabel = formatProfitLossComparisonColumnLabel(
      period.start,
      period.end,
      groupBy,
      timeZone
    );
    if (columnLabel) {
      reportData.reportDetails.dateRange = columnLabel;
      reportData.reportDetails.titleFull = `${reportData.reportDetails.title} from ${columnLabel}`;
      reportData.reportDetails.filename =
        `${reportData.reportDetails.title}-${columnLabel}.pdf`
          .toLowerCase()
          .replace(/[-/ ]/g, "-");
    }

    return reportData;
  });

  return {
    type: "profit/loss-comparison",
    groupBy,
    data,
  };
}

// https://www.faa.gov/air_traffic/publications/atpubs/cnt_html/appendix_a.html
const StateToAbbreviation: Record<string, string> = {
  alabama: "AL",
  kentucky: "KY",
  ohio: "OH",
  alaska: "AK",
  louisiana: "LA",
  oklahoma: "OK",
  arizona: "AZ",
  maine: "ME",
  oregon: "OR",
  arkansas: "AR",
  maryland: "MD",
  pennsylvania: "PA",
  "american samoa": "AS",
  massachusetts: "MA",
  "puerto rico": "PR",
  california: "CA",
  michigan: "MI",
  "rhode island": "RI",
  colorado: "CO",
  minnesota: "MN",
  "south carolina": "SC",
  connecticut: "CT",
  mississippi: "MS",
  "south dakota": "SD",
  delaware: "DE",
  missouri: "MO",
  tennessee: "TN",
  "district of columbia": "DC",
  montana: "MT",
  texas: "TX",
  florida: "FL",
  nebraska: "NE",
  "trust territories": "TT",
  georgia: "GA",
  nevada: "NV",
  utah: "UT",
  guam: "GU",
  "new hampshire": "NH",
  vermont: "VT",
  hawaii: "HI",
  "new jersey": "NJ",
  virginia: "VA",
  idaho: "ID",
  "New Mexico": "NM",
  "virgin islands": "VI",
  illinois: "IL",
  "new york": "NY",
  washington: "WA",
  indiana: "IN",
  "north carolina": "NC",
  "West Virginia": "WV",
  iowa: "IA",
  "North Dakota": "ND",
  wisconsin: "WI",
  kansas: "KS",
  "northern mariana islands": "MP",
  wyoming: "WY",
};

function buildSalesTaxReport(
  reportType: "sales-tax",
  parameters: ParametersSalesTax,
  sales: Sale[],
  user?: User
): SalesTaxReport {
  const timeZone = getEffectiveTimeZoneFromUser(user);
  const title = "Sales Tax Report";
  const dateRange = `${renderZonedOnlyDate(
    parameters.startDate,
    timeZone
  )} to ${renderZonedOnlyDate(parameters.endDate, timeZone)}`;
  const report = {
    type: "sales-tax" as const,
    data: {
      reportDetails: {
        title,
        titleFull: `${title} from ${dateRange}`,
        filename: `${title}-${dateRange}.pdf`
          .toLowerCase()
          .replace(/[-/ ]/g, "-"),
        dateRange,
      },
      salesByState: {},
      taxByState: {},
    },
  };

  const startDate = startOfCalendarDayContainingInstant(
    new Date(parameters.startDate),
    timeZone
  );
  const endDate = endOfCalendarDayContainingInstant(
    new Date(parameters.endDate),
    timeZone
  );

  for (const sale of sales) {
    const date = new Date(sale.sale_date || 0);
    if (date < startDate || date > endDate) continue;
    const salesTax = sale.sales_tax;
    if (!salesTax) continue;
    const state = sale.sale_state
      ? StateToAbbreviation[sale.sale_state.toLowerCase()] ||
        sale.sale_state.toUpperCase().replace(/[^A-Z]/g, "")
      : "No data";
    if (!report.data.taxByState[state]) {
      report.data.taxByState[state] = {
        liableToPay: 0,
        nonLiableToPay: 0,
      };
    }
    if (!report.data.salesByState[state]) report.data.salesByState[state] = [];

    report.data.taxByState[state][
      sale.liable_to_pay ? "liableToPay" : "nonLiableToPay"
    ] += salesTax;

    report.data.salesByState[state].push(sale);
  }

  return report;
}

/** Live P&L from Redux + parameters frozen on the successful live run result. */
export const profitLossReportSelector = createSelector(
  [
    getAllSales,
    expensesSelector,
    generalLedgerAccountsSelector,
    selectUser,
    parametersInput,
  ],
  (sales, expenses, generalLedgerAccounts, user, parameters) =>
    buildProfitLossReport(
      "profit/loss",
      parameters,
      generalLedgerAccounts,
      sales,
      expenses,
      user
    )
);

export const profitLossComparisonReportSelector = createSelector(
  [
    getAllSales,
    expensesSelector,
    generalLedgerAccountsSelector,
    selectUser,
    parametersInput,
  ],
  (sales, expenses, generalLedgerAccounts, user, parameters) =>
    buildProfitLossComparisonReport(
      "profit/loss-comparison",
      {
        ...parameters,
        groupBy: parameters.groupBy || "month",
      },
      generalLedgerAccounts,
      sales,
      expenses,
      user
    )
);

export const salesTaxReportSelector = createSelector(
  [getAllSales, selectUser, parametersInput],
  (sales, user, parameters) =>
    buildSalesTaxReport("sales-tax", parameters, sales, user)
);
